/**
 * PATH: packages/ui/src/kanban/components/board-view/dockPrefs.ts
 * FILE_ID: UI-KANBAN-DOCK-PREFS-001
 * PURPOSE: Per-operator prefs for the floating board dock — which items
 *          show as icons, and the escape hatch back to the classic
 *          full-width bottom bar. Storage + a hook that keeps every
 *          mounted dock in sync.
 * WHY: FEAT-2026-07-03-KANBAN-FLOATING-DOCK (operator: "The strip …
 *      should be customisable · by default … a simple overlaying hover
 *      that sits as a dock in the centre bottom of the screen with
 *      icons … and you can customise what goes there").
 * FLOW:
 *   ┌────────────┐   ┌──────────────────────────┐   ┌──────────────────┐
 *   │ userId     │ → │ localStorage             │ → │ { hidden[],      │
 *   │ + toggles  │   │ kanban:prefs:<segment>   │   │   classicBar }   │
 *   └────────────┘   └──────────────────────────┘   └──────────────────┘
 * DEPENDENCIES:
 *   - React useSyncExternalStore: tear-free reads across mounts.
 * RELATED:
 *   - BoardDock.tsx: consumes prefs; hosts the customise panel.
 *   - card-detail/openModePref.ts: the `kanban:prefs:<segment>` JSON bag
 *     convention this joins (one bag per user, `anon` fallback — see
 *     BUG-2026-07-03-KANBAN-OPEN-MODE-DEAD-WITHOUT-IDENTITY). Add keys
 *     to the SAME object, do not mint a second storage key.
 */
import { useCallback, useSyncExternalStore } from 'react'

/** Everything the dock can show. Launchers first, then views, then nav. */
export const DOCK_ITEM_IDS = [
  'mycards',
  'planner',
  'notebooks',
  'filters',
  'emails',
  'board',
  'gantt',
  'smarttime',
  'timeline',
  'swimlanes',
  'table',
  'calendar',
  'cashflow',
  'dashboard',
  'switch',
] as const

export type DockItemId = (typeof DOCK_ITEM_IDS)[number]

export interface KanbanDockPrefs {
  /** Items the operator hid from the dock (still reachable via ⋮). */
  hidden: DockItemId[]
  /** true = render the legacy full-width bottom bar instead of the dock. */
  classicBar: boolean
}

export const DEFAULT_DOCK_PREFS: KanbanDockPrefs = { hidden: [], classicBar: false }

const CHANGE_EVENT = 'kanban:dock-prefs-changed'

function userSegment(userId: string | number | null | undefined): string {
  return userId === null || userId === undefined || userId === ''
    ? 'anon'
    : String(userId)
}

function storageKey(userId: string | number | null | undefined): string {
  return `kanban:prefs:${userSegment(userId)}`
}

function readPrefsBag(
  userId: string | number | null | undefined,
): Record<string, unknown> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

function coerce(raw: unknown): KanbanDockPrefs {
  if (!raw || typeof raw !== 'object') return DEFAULT_DOCK_PREFS
  const o = raw as Record<string, unknown>
  const hidden = Array.isArray(o.hidden)
    ? (o.hidden.filter((id): id is DockItemId =>
        (DOCK_ITEM_IDS as readonly string[]).includes(String(id)),
      ) as DockItemId[])
    : []
  return { hidden, classicBar: o.classicBar === true }
}

export function readKanbanDockPrefs(
  userId: string | number | null | undefined,
): KanbanDockPrefs {
  return coerce(readPrefsBag(userId).dock)
}

export function writeKanbanDockPrefs(
  userId: string | number | null | undefined,
  prefs: KanbanDockPrefs,
): void {
  if (typeof window === 'undefined') return
  try {
    const bag = readPrefsBag(userId)
    bag.dock = prefs
    window.localStorage.setItem(storageKey(userId), JSON.stringify(bag))
  } catch {
    /* quota / disabled storage — the event below still updates this
       session's mounted docks; the pref just won't survive reload. */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

/** Pure toggle helper — returns a NEW prefs object. */
export function toggleDockItem(
  prefs: KanbanDockPrefs,
  id: DockItemId,
): KanbanDockPrefs {
  const hidden = prefs.hidden.includes(id)
    ? prefs.hidden.filter((h) => h !== id)
    : [...prefs.hidden, id]
  return { ...prefs, hidden }
}

/**
 * [prefs, setPrefs] backed by localStorage + a change event so the dock,
 * the customise panel, and any future settings surface stay in sync.
 * Serialized snapshot cache keeps useSyncExternalStore referentially
 * stable between writes.
 */
let snapshotCache: { key: string; raw: string; value: KanbanDockPrefs } | null = null

function getCachedPrefs(userId: string | number | null | undefined): KanbanDockPrefs {
  const key = storageKey(userId)
  let raw = ''
  if (typeof window !== 'undefined') {
    try {
      raw = window.localStorage.getItem(key) ?? ''
    } catch {
      raw = ''
    }
  }
  if (snapshotCache && snapshotCache.key === key && snapshotCache.raw === raw) {
    return snapshotCache.value
  }
  const value = readKanbanDockPrefs(userId)
  snapshotCache = { key, raw, value }
  return value
}

export function useKanbanDockPrefs(
  userId: string | number | null | undefined,
): [KanbanDockPrefs, (next: KanbanDockPrefs) => void] {
  const subscribe = useCallback((onChange: () => void) => {
    if (typeof window === 'undefined') return () => {}
    window.addEventListener(CHANGE_EVENT, onChange)
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [])
  const getSnapshot = useCallback(() => getCachedPrefs(userId), [userId])
  const prefs = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_DOCK_PREFS)
  const setPrefs = useCallback(
    (next: KanbanDockPrefs) => writeKanbanDockPrefs(userId, next),
    [userId],
  )
  return [prefs, setPrefs]
}
